import * as React from 'react';
import {Provider, useSelector, useDispatch} from 'react-redux';

import HabitCalendar from '../../components/HabitCalendar';
import WeeklyNavigation from '../../components/WeeklyNavigation';
import ProgressBar from '../../components/ProgressBar';
import HabitCard from '../../components/HabitCard';
import Button from '../../components/Button';
import {HABIT_TYPE} from '../../features/Habit/constants';
import Dialog from '../../components/Dialog';
import {dayToLabel} from '../../utils/day-to-label';
import DailyNavigation from '../../components/DailyNavigation';
import {getStartEndWeek} from '../../utils/get-start-end-week';
import store from '../../store/index';
import { goToNextWeek, goToPrevWeek, setPeriod } from '../../features/WeeklyPeriod/actions';
import { gotToNextDate, goToPrevDate} from '../../features/CurrentDate/actions'
import { undoComplete} from '../../features/Checklist/actions';
import { removeHabit} from '../../features/Habit/actions';
import { isHabitCompleted } from '../../utils/is-habit-completed';
import { getTodayHabits } from '../../utils/get-today-habits';
import { getDailyPercentage } from '../../utils/get-daily-percentage';
import {getWeeklyPercentage} from '../../utils/get-weekly-percentage';
import {fetchHabits, saveHabit, saveMarkComplete, fetchChecklists, saveUndoComplete, requestDeleteHabit} from '../../features/Request/actions';

const toChecklist = (acc, curr) => {
  let date = new Date(); 
  date.setDate(date.getDate() - 1)
  acc[curr.uuid] = [date]
  return acc;
}


export const Default = () => {

  return <Provider store={store}>
    <Component/>
  </Provider>
}

const Component = props => {

  const initialNewHabit = {
     name: '', 
     type: HABIT_TYPE.DO, 
     recurrence: []
  }

  let [startWeek, endWeek] = useSelector(state => state.weeklyPeriod);
  let habits = useSelector(state => state.habits);
  let checklists = useSelector(state => state.checklists);
  let currentDate = useSelector(state => state.currentDate);

  let dispatch = useDispatch();

  let [addDialog, setAddDialog] = React.useState(false);
  let [newHabit, setNewHabit] = React.useState(initialNewHabit);

  // HANDLERS 
  
  const handleNextDay = _ => {
    dispatch(gotToNextDate());
    const nextDay = new Date(currentDate);
          nextDay.setDate(nextDay.getDate() + 1);
    dispatch(setPeriod(getStartEndWeek(nextDay)))
  }

  const handlePrevDay =_ => {
    dispatch(goToPrevDate());
    const prevDay = new Date(currentDate);
          prevDay.setDate(prevDay.getDate() - 1);
    dispatch(setPeriod(getStartEndWeek(prevDay)))
  }

  const handleNextWeek = () => dispatch(goToNextWeek());

  const handlePrevWeek = () => dispatch(goToPrevWeek());

  const handleMarkComplete = habit => dispatch(saveMarkComplete(habit, currentDate)); 

  // TODO: update to save undo to database
  const handleUndoComplete = habit => dispatch(saveUndoComplete(habit, currentDate));

  const updateNewHabitField = (field, value) => {
      setNewHabit({...newHabit, [field]: value});
  }

  const handleHabitName = e => updateNewHabitField('name', e.target.value);
  const handleHabitType = e => updateNewHabitField('type', e.target.value);
  const handleHabitRecurrence = (dayOfWeek) => {
      if(newHabit.recurrence.indexOf(dayOfWeek) > -1){
        return setNewHabit({...newHabit, recurrence: newHabit.recurrence.filter(day => day !== dayOfWeek)});
      }
      setNewHabit({...newHabit, recurrence: [...newHabit.recurrence, dayOfWeek ].sort()})
  }

  const handleAddHabit = async () => {
    dispatch(saveHabit({...newHabit, created_at: new Date()}))
    setAddDialog(false);
    setNewHabit(initialNewHabit);
  }

  const handleDeleteHabit = (habit) => {
    dispatch(requestDeleteHabit(habit))
  }

  const handleCancelAddHabit = () => {
    setNewHabit(initialNewHabit)
  }

  React.useEffect(() => {
    dispatch(fetchHabits());
    dispatch(fetchChecklists());
  }, [])

  return <div className="rounded md:shadow-xl w-full md:w-3/4 mx-auto md:flex md:border-white mt-20">
    <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
    <div className="w-full md:w-2/3 pr-8 p-3 bg-white mb-10">
      <h3 className="text-2xl mb-6">Hi, Azamuddin</h3>
      <div className="text-right">
         <Button fullRound onClick={_ => setAddDialog(true)}>
           <span className="font-bold text-blue-600">
             + Add Habit
           </span>
         </Button>
      </div>
      <WeeklyNavigation startDate={startWeek} endDate={endWeek} onRequestNext={handleNextWeek} onRequestPrev={handlePrevWeek}/>
      <br/>
      <ProgressBar percentage={getWeeklyPercentage(startWeek, habits, checklists)}/>
      <div className="text-right mb-5">
        <small>{(getWeeklyPercentage(startWeek, habits, checklists) * 100).toFixed(0)}% achieved</small>
      </div>
      <HabitCalendar 
         startDate={startWeek} 
         endDate={endWeek} 
         habits={habits} 
         checklists={checklists}
         onDeleteHabit={handleDeleteHabit}
      />
    </div>

    <div className="w-full md:w-1/3 md:border-l p-3 bg-gray-200 rounded-r xs:shadow-xl xs:rounded-l">

      <DailyNavigation 
        date={currentDate}  
        onRequestNext={handleNextDay} 
        onRequestPrev={handlePrevDay} 
      />

      <ProgressBar percentage={getDailyPercentage(currentDate, habits, checklists)}/>
      <div className="text-right">
        <small>{(getDailyPercentage(currentDate, habits, checklists) * 100).toFixed(0)}% of daily goals achieved</small>
      </div>
      <br/>
      {getTodayHabits(currentDate, habits).map(habit => ({...habit, completed: isHabitCompleted(habit.id, currentDate, checklists)})).map((habit, index) => {
        return <HabitCard 
          key={index} 
          habit={habit}
          onMarkComplete={handleMarkComplete} 
          onUndoComplete={handleUndoComplete} 
         />
      })} 
    </div>

    <Dialog 
      isOpen={addDialog} 
      onRequestClose={_ => setAddDialog(false)} title="Add new Habit"
      onConfirm={handleAddHabit}
      onCancel={handleCancelAddHabit}
      confirmText="Simpan"
      cancelText="Batal"
    >
      <h4 className="text-xl">Habit name</h4>
      <input type="text" className="border rounded p-2 w-full" placeholder="Coding at night" onChange={handleHabitName}/>
      <br/>
      <br/>
      <h4 className="text-xl">Type</h4>
      <select onChange={handleHabitType}>
        <option value={HABIT_TYPE.DO}>To do </option>
        <option value={HABIT_TYPE.DONT}>To don't </option>
      </select>
      <br />
      <br />

      <h4 className="text-xl">Recurrence</h4>
      {Array.from({length: 7}).map((_, index) => {
           return <div className="inline-block mr-2 w-16" key={index}>
             <Button fitContainer onClick={handleHabitRecurrence.bind(null, index)} active={newHabit.recurrence.indexOf(index) > -1} >
               {dayToLabel(index)}
             </Button>
          </div>
      })}
    </Dialog>
  </div>
}

export default Component
